import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { TransactionType } from 'dashboard/models/transaction-type';
import { TriggerComputedField } from 'dashboard/models/trigger';
import { WorkflowVersionOutput } from 'dashboard/models/workflow-version';
import { getJsonSchemaForFieldSchema } from 'lib/fields';
import { JsonSchemaObject } from 'lib/json-schema';

const Row = styled.div`
  display: flex;
  align-items: center;
`;

interface ResultOutputProps {
  editable: boolean;
  jsonSchema: JsonSchemaObject;
  outputs: WorkflowVersionOutput[];
  tokensToDisplayValue: Record<string, string>;
  transactionTypes: TransactionType[];
  trigger: TriggerComputedField;
  updateOutput: (outputs: WorkflowVersionOutput[]) => void;
}

const ResultOutput: FC<ResultOutputProps> = ({
  editable,
  jsonSchema,
  outputs,
  tokensToDisplayValue,
  transactionTypes = [],
  trigger,
  updateOutput,
}) => {
  const [inputValue, setInputValue] = useState<string | string[]>(
    outputs?.[0]?.['method-chain'] || '',
  );

  const schemaType = useMemo(() => {
    const fieldSchema = transactionTypes
      .find(
        (transactionType) => transactionType.id === trigger.attributes['field-schema-object-token'],
      )
      ?.attributes?.[
        'field-schemas'
      ]?.find((schema) => schema.key === trigger.attributes['field-key']);
    if (!fieldSchema) {
      return null;
    }
    const jsonSchema = getJsonSchemaForFieldSchema(fieldSchema);
    return 'type' in jsonSchema ? jsonSchema.type : null;
  }, [trigger, transactionTypes]);

  const onInputChange = (value: string) => {
    // JsonSchemaSelect does not call onFieldSelect when deleting the inputValue. Capture the case
    // via onValueChange and manually update output.
    if (value === '') {
      updateOutputResult('');
    }
    setInputValue(value);
  };

  const updateOutputResult = (value: string) => {
    updateOutput([
      {
        token: 'result',
        name: 'result',
        'method-chain': value,
      },
    ]);
  };

  return (
    <>
      <Row>
        <ParamLabel htmlFor='workflow-output-config__result-input' labelText='Result' />
        <HelpIcon>This will be saved as the computed field’s value</HelpIcon>
      </Row>
      <JsonSchemaSelect
        id='workflow-output-config__result'
        disabled={!editable}
        inputValue={inputValue}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onValueChange={onInputChange}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onFieldSelect={(option: Option) => updateOutputResult(option.value)}
        allowedTypes={schemaType ? [schemaType] : []}
        canSelectObjects={false}
      />
    </>
  );
};

export default ResultOutput;
